import { Calendar, RefreshCw, Wallet, Clock, CheckCircle2, Sparkles, MessageCircle } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const GuaranteeSection = () => {
  const { t } = useLanguage();

  const guarantees = [
    {
      icon: Calendar,
      title: t('guarantee.satisfaction.title'),
      description: t('guarantee.satisfaction.desc'),
      gradient: "from-accent/20 to-accent/5",
      iconBg: "bg-accent/10"
    },
    {
      icon: RefreshCw,
      title: t('guarantee.revisions.title'),
      description: t('guarantee.revisions.desc'),
      gradient: "from-accent/15 to-accent/5",
      iconBg: "bg-accent/10"
    },
    {
      icon: Wallet,
      title: t('guarantee.moneyBack.title'),
      description: t('guarantee.moneyBack.desc'),
      gradient: "from-accent/20 to-accent/5",
      iconBg: "bg-accent/10"
    },
    {
      icon: Clock,
      title: t('guarantee.delivery.title'),
      description: t('guarantee.delivery.desc'),
      gradient: "from-accent/15 to-accent/5",
      iconBg: "bg-accent/10"
    }
  ];

  return (
    <section className="py-12 md:py-16 lg:py-20 bg-gradient-to-br from-background via-accent/5 to-background relative overflow-hidden">
      {/* Subtle background elements */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-10 left-10 w-48 h-48 bg-accent/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 right-10 w-64 h-64 bg-accent/10 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-8 md:mb-10 lg:mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-accent/10 rounded-full mb-4">
            <Sparkles className="w-3 h-3 text-accent" />
            <span className="text-xs font-modern font-semibold text-accent uppercase tracking-wider">
              {t('guarantee.badge')}
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-outline text-foreground mb-3 uppercase tracking-tight leading-tight" style={{ fontWeight: 900 }}>
            <span className="relative inline-block">
              <span className="relative z-10">{t('guarantee.title')}</span>
              <span className="absolute bottom-1 left-0 w-full h-3 bg-accent/30 -z-0"></span>
            </span>
          </h2>
          <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto font-modern font-medium leading-relaxed">
            {t('guarantee.subtitle')}
          </p>
        </div>

        {/* Guarantee Grid - Modern cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5 lg:gap-6 mb-8 md:mb-10 max-w-5xl mx-auto">
          {guarantees.map((guarantee, index) => (
            <div 
              key={guarantee.title}
              className="group relative bg-card border border-border rounded-2xl p-5 lg:p-6 hover:border-accent/50 transition-all duration-500 hover:shadow-xl hover:shadow-accent/10 hover:-translate-y-1"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              {/* Gradient overlay */}
              <div className={`absolute inset-0 bg-gradient-to-br ${guarantee.gradient} rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500`}></div>
              
              <div className="relative z-10">
                {/* Icon */}
                <div className="mb-4">
                  <div className={`inline-flex items-center justify-center w-10 h-10 ${guarantee.iconBg} rounded-xl group-hover:scale-110 group-hover:rotate-3 transition-all duration-300`}>
                    <guarantee.icon className="w-5 h-5 text-accent" strokeWidth={2.5} />
                  </div>
                </div>

                {/* Content */}
                <h3 className="text-lg font-modern font-bold text-foreground mb-2 tracking-tight">
                  {guarantee.title}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed font-modern">
                  {guarantee.description}
                </p>
              </div>

              {/* Corner accent */}
              <div className="absolute top-0 right-0 w-20 h-20 bg-accent/5 rounded-bl-3xl rounded-tr-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            </div>
          ))}
        </div>

        {/* Bottom CTA - Modern design */}
        <div className="relative max-w-4xl mx-auto">
          <div className="relative bg-gradient-to-br from-accent via-accent to-accent/90 rounded-2xl p-6 lg:p-8 overflow-hidden">
            {/* Background pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute inset-0" style={{
                backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
                backgroundSize: '32px 32px'
              }}></div>
            </div>

            <div className="relative z-10">
              <h3 className="text-2xl md:text-3xl font-modern font-black text-white mb-2 text-center tracking-tight">
                {t('guarantee.cta.title')}
              </h3>
              <p className="text-sm md:text-base text-white/90 mb-6 text-center font-modern font-medium">
                {t('guarantee.cta.description')}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm px-4 py-3 rounded-xl border border-white/20">
                  <div className="flex items-center justify-center w-8 h-8 bg-white/20 rounded-lg">
                    <CheckCircle2 className="w-4 h-4 text-white" strokeWidth={2.5} />
                  </div>
                  <span className="font-modern font-bold text-white text-sm">{t('guarantee.cta.noRisk')}</span>
                </div>
                <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm px-4 py-3 rounded-xl border border-white/20">
                  <div className="flex items-center justify-center w-8 h-8 bg-white/20 rounded-lg">
                    <MessageCircle className="w-4 h-4 text-white" strokeWidth={2.5} />
                  </div>
                  <span className="font-modern font-bold text-white text-sm">{t('guarantee.cta.guaranteed')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GuaranteeSection;

